import FormField, { type FormFieldProps } from "@common/controls/Form/Field/component";
import { type InputTypeProps } from "@common/declarations";
import { convertFromAmountInCents } from "@common/lib/currency";
import { usePromise } from "@common/lib/hooks";
import { joinDefined } from "@common/lib/misc";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { type StripeElementsOptions } from "@stripe/stripe-js";
import React from "react";
import StripeCardInput from "./Input";
import { initStripe, type StripeCustomerInfo } from "./lib";

export type CreateStripeIntentResponse = {
  isSetup: boolean;
  secret: string;
};
export type CreateStripeIntent = () => Promise<CreateStripeIntentResponse>;

type PropsBase = Omit<
  FormFieldProps<string | null | undefined, InputTypeProps<string>>,
  "Component" | "componentOpts" | "validator"
> & {
  createIntent: CreateStripeIntent;
  customer?: StripeCustomerInfo;
  inputStyle?: React.CSSProperties;
};
const StripeCardFormFieldBase = ({ createIntent, customer, inputStyle, ...fieldProps }: PropsBase) => {
  const stripe = useStripe();
  const elements = useElements();

  // TODO: a better approach.
  // We don't want getFinalValue to create a new intent on subsequent form submissions (in case of failures).
  const [intentId, setIntentId] = React.useState<string | null>(null);

  const getFinalValue = React.useCallback(async () => {
    if (intentId) return intentId;

    if (!stripe || !elements) return null;

    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement("card")!,
      billing_details: customer
        ? {
            email: customer.email,
            name: joinDefined(" ", customer.firstName, customer.lastName)
          }
        : undefined
    });
    if (paymentMethodError) throw new Error(paymentMethodError.message);

    const intentResponse = await createIntent();
    if (intentResponse.isSetup) {
      const res = await stripe.confirmCardSetup(intentResponse.secret, {
        payment_method: paymentMethod.id
      });

      if (res.error) throw new Error(res.error.message);
      setIntentId(res.setupIntent.id);
      return res.setupIntent.id;
    }

    const res = await stripe.confirmCardPayment(intentResponse.secret, {
      payment_method: paymentMethod.id,
      setup_future_usage: "off_session",
      save_payment_method: true
    });

    if (res.error) throw new Error(res.error.message);
    setIntentId(res.paymentIntent.id);
    return res.paymentIntent.id;
  }, [stripe, elements, customer, createIntent, intentId, fieldProps.value]);

  return (
    <FormField
      {...fieldProps}
      Component={StripeCardInput}
      getFinalValue={getFinalValue}
      componentProps={{ style: inputStyle }}
      value="dummy"
    />
  );
};

export type StripeCardInputFormFieldProps = PropsBase & {
  amount?: number;
  currencyCode?: string;
  fontCssSrc?: string;
  publishableKey: string;
};
const StripeCardFormField = ({ publishableKey, fontCssSrc, ...props }: StripeCardInputFormFieldProps) => {
  const [stripePromise] = usePromise({
    fn: initStripe,
    args: [publishableKey],
    shouldLoad: true
  });

  const options = React.useMemo(
    (): StripeElementsOptions => ({
      amount:
        props.amount && props.currencyCode ? convertFromAmountInCents(props.amount, props.currencyCode) : undefined,
      currency: props.currencyCode?.toLowerCase(),
      fonts: fontCssSrc
        ? [
            {
              cssSrc: fontCssSrc
            }
          ]
        : undefined
    }),
    [fontCssSrc, props.amount, props.currencyCode]
  );

  return (
    <Elements options={options} stripe={stripePromise}>
      <StripeCardFormFieldBase {...props} />
    </Elements>
  );
};
export default StripeCardFormField;
